import type { RadioButtonGroupProps } from '@sunwebgroup/uds-ui/radio-button'
import { Accordion, Button, RadioButtonGroup } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { ShowMore } from '@/scopes/common/components/ShowMoreUDS'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useSelectFilter } from '@/scopes/search/search/domain/hooks'
import { createSearchFilterClickEvent } from '../../../../domain/analytics'
import { FilterGroupProps } from '../types'
import ListFilterItem from './ListFilterItem'

const ClearButton = styled(Button)`
  display: flex;
  margin: 0 auto;
  margin-top: 8px;
`

export function CustomerRatingFilter({ defaultOpen }: FilterGroupProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, options, selectedValue, setSelection } = useSelectFilter('CustomersRating')

  const handleFilterChange: RadioButtonGroupProps['onValueChange'] = (value) => {
    setSelection(value)
    sendEvent(
      createSearchFilterClickEvent({
        element: 'radio button',
        type: 'ratings',
        result: value,
      }),
    )
  }

  const handleButtonClick = React.useCallback(() => {
    setSelection(null)
  }, [setSelection])

  if (isEmpty(options))
    return null

  return (
    <Accordion.Item
      title={intl.formatMessage({ id: 'accommodation_rating_label' })}
      defaultOpen={selectedValue !== null || defaultOpen}
      data-qa="customer-rating-filter"
    >
      {/* Null value is not accepted, but makes it possible to reset the radio group. See also this open issue: https://github.com/radix-ui/primitives/issues/1588. */}
      <RadioButtonGroup value={selectedValue ?? null} onValueChange={handleFilterChange}>
        <ShowMore
          openLabel={intl.formatMessage({ id: 'show_more' })}
          closeLabel={intl.formatMessage({ id: 'show_less' })}
          visibleItems={4}
        >
          {options.map(({ name, value, resultsCount }) => (
            <ListFilterItem.Radio
              counter={resultsCount}
              key={value}
              value={value}
              label={name}
              disabled={isFetching}
              data-qa="customer-rating-filter-option"
              data-qa-value={value?.toLowerCase()}
            />
          ))}
        </ShowMore>
        {selectedValue && !isFetching && (
          <ClearButton variant="secondary" onClick={handleButtonClick}>
            <Button.Icon glyph="DismissSmall" />
            {intl.formatMessage({ id: 'search_filter_remove_selection' })}
          </ClearButton>
        )}
      </RadioButtonGroup>
    </Accordion.Item>
  )
}
